import React from "react";
import PropTypes from "prop-types";
import { useNavigate, useSearchParams } from "react-router-dom";
import { connect } from "react-redux";
import styled from "styled-components";
import ListCard from "../../common/ListCard";
import LinkedText from "../../common/LinkedText";
import RestaurantCard from "../mobile/RestaurantCard";
import { slugify } from "../../../util/slugify";
import {
  filterByLeafRating,
  filterBySelectedCuisines,
} from "../../../util/restaurantFilters";

const Container = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 16px;
`;

const Title = styled.h1`
  font-size: 1.6em;
  margin: 4px 0 6px;
  color: #1a1a1a;
  overflow-wrap: anywhere;
`;

const CityLine = styled.div`
  color: var(--color-text-secondary, #666);
  font-size: 0.9em;
  margin: 0 0 14px;
`;

const Prose = styled.div`
  p {
    line-height: 1.55;
    color: #333;
    margin: 0 0 14px;
  }
`;

const SectionHeading = styled.h2`
  font-size: 1.15em;
  margin: 24px 0 12px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
  margin: 16px 0;
`;

const ListOverviewPanel = ({
  savedSearch,
  displayCity,
  restaurants,
  selectedCuisines,
  minLeafCount,
  verificationTier,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const proseParagraphs = (savedSearch.description || "")
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);

  const mapParams = new URLSearchParams(searchParams);
  mapParams.delete("view");
  const mapSearch = mapParams.toString();
  const mapHref = mapSearch ? `?${mapSearch}` : `?`;

  const curatedIds = (savedSearch.curatedRestaurantIds || []).map((id) =>
    typeof id === "string" ? id : id.toString()
  );
  const restaurantsToShow = (restaurants || [])
    .filter((r) => {
      if (curatedIds.length === 0) return true;
      return curatedIds.includes(r._id?.toString());
    })
    .filter((r) => filterBySelectedCuisines(r, selectedCuisines))
    .filter((r) => filterByLeafRating(r, minLeafCount, verificationTier));

  return (
    <Container>
      <Title>{savedSearch.name}</Title>
      {displayCity && <CityLine>{displayCity}</CityLine>}
      <Prose>
        {proseParagraphs.length > 0 ? (
          proseParagraphs.map((p, i) => (
            <p key={i}>
              <LinkedText text={p} />
            </p>
          ))
        ) : null}
      </Prose>
      {restaurantsToShow.length > 0 && (
        <>
          <SectionHeading>
            {restaurantsToShow.length} restaurant
            {restaurantsToShow.length !== 1 ? "s" : ""} in this list
          </SectionHeading>
          <Grid>
            {restaurantsToShow.map((r) => (
              <RestaurantCard
                key={r._id}
                restaurant={r}
                showDistance={false}
                onClick={() =>
                  navigate(`/details/${r._id}/${r.slug || slugify(r.name)}`)
                }
              />
            ))}
          </Grid>
        </>
      )}
      <SectionHeading>Explore further</SectionHeading>
      <ListCard
        to={mapHref}
        title="See the full list on the map"
        description="Browse every restaurant in this list with location, hours, and menu highlights."
        icon="fas fa-map"
        iconLabel="Map view"
      />
    </Container>
  );
};

ListOverviewPanel.propTypes = {
  savedSearch: PropTypes.shape({
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    curatedRestaurantIds: PropTypes.array,
  }).isRequired,
  displayCity: PropTypes.string,
  restaurants: PropTypes.array,
  selectedCuisines: PropTypes.object,
  minLeafCount: PropTypes.number,
  verificationTier: PropTypes.string,
};

const mapStateToProps = (state) => ({
  restaurants: state.restaurant.restaurants,
  selectedCuisines: state.selectedCuisines,
  minLeafCount: state.filters.minLeafCount,
  verificationTier: state.filters.verificationTier,
});

export default connect(mapStateToProps)(ListOverviewPanel);
